import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { IntegrationsService } from './IntegrationsApi';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';

const initialState: any = {
    integrationsAreLoading: false,
    integrationIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchIntegrations = createAsyncThunk<any, IPaginationPayload>(
    'integrations/tryToFetchIntegrations',
    async ({ pageNumber, pageSize, filters }) => {
        const result = await IntegrationsService.tryToFetchIntegrationsWithPagination(pageNumber, pageSize, filters);
        return result?.data;
    },
);

export const tryToFetchAllIntegrations = createAsyncThunk<any, any>(
    'integrations/tryToFetchAllIntegrations',
    async () => {
        const result = await IntegrationsService.tryToFetchAllPartnerIntegrations();
        return result?.data?.data;
    },
);

export const tryToFetchSingleIntegration = createAsyncThunk<any, string>(
    'integrations/tryToFetchSingleIntegration',
    async (id: string) => {
        const result = await IntegrationsService.tryToFetchSingleIntegration(id);
        return result?.data?.data;
    },
);

export const tryToAddIntegration = createAsyncThunk<ApiResponse<any>, any>(
    'integrations/tryToAddIntegration',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await IntegrationsService.tryToAddIntegration(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditIntegration = createAsyncThunk<ApiResponse<any>, any>(
    'integrations/tryToEditIntegration',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await IntegrationsService.tryToEditIntegration(id, data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToDeleteIntegration = createAsyncThunk<ApiResponse<any>, string>(
    'integrations/tryToDeleteIntegration',
    async (id, { rejectWithValue }) => {
        try {
            const result = await IntegrationsService.tryToDeleteIntegration(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const integrationsSlice = createSlice({
    name: 'integrations',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchIntegrations
            .addCase(tryToFetchIntegrations.pending, (state) => {
                state.integrationsAreLoading = true;
            })
            .addCase(tryToFetchIntegrations.rejected, (state) => {
                state.integrationsAreLoading = false;
            })
            .addCase(tryToFetchIntegrations.fulfilled, (state, action) => {
                state.integrationsAreLoading = false;
                state.integrations = action.payload;
            })

            // tryToFetchSingleIntegration
            .addCase(tryToFetchSingleIntegration.pending, (state) => {
                state.integrationIsLoading = true;
            })
            .addCase(tryToFetchSingleIntegration.rejected, (state) => {
                state.integrationIsLoading = false;
                state.integrationDetails = undefined;
            })
            .addCase(tryToFetchSingleIntegration.fulfilled, (state, action) => {
                state.integrationIsLoading = false;
                state.integrationDetails = action.payload;
            })

            // tryToAddIntegration
            .addCase(tryToAddIntegration.pending, (state) => {
                state.integrationIsLoading = true;
            })
            .addCase(tryToAddIntegration.rejected, (state) => {
                state.integrationIsLoading = false;
            })
            .addCase(tryToAddIntegration.fulfilled, (state, action) => {
                state.integrationIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg;
                if (state.integrations?.data) state.integrations.data.elements = [data, ...state.integrations?.data.elements || []];
            })

            // tryToDeleteStore
            .addCase(tryToDeleteIntegration.rejected, (state) => {
                state.integrationIsLoading = false;
                state.requestStatus = 'error';
            })
            .addCase(tryToDeleteIntegration.fulfilled, (state, action) => {
                state.integrationIsLoading = false;
                state.requestStatus = 'success';
                if (state.integrations?.data.elements)
                    state.integrations.data.elements =
                        { ...state }.integrations?.data.elements.filter((integration: any) => integration?._id !== action.meta.arg) || [];
            })

            // tryToEditIntegration
            .addCase(tryToEditIntegration.pending, (state) => {
                state.integrationIsLoading = true;
            })
            .addCase(tryToEditIntegration.rejected, (state) => {
                state.integrationIsLoading = false;
            })
            .addCase(tryToEditIntegration.fulfilled, (state, action) => {
                state.integrationIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg?.data;
                if (state.integrations?.data.elements)
                    state.integrations.data.elements =
                        { ...state }.integrations?.data.elements.map((integration: any) => {
                            if (integration?._id === action.meta?.arg?.id) {
                                return {
                                    ...data || integration || {},
                                }
                            } else {
                                return integration
                            }
                        }) || [];
            })
    },
});

export default integrationsSlice.reducer;